/**
 * KhaoYaiPanel — Khao Yai National Park status badge.
 *
 * UNESCO World Heritage Site badge, visitor count, peak season indicator.
 * Sikhio is the nearest municipality town (~35 km); the park is a major
 * driver of tourism revenue and regional identity.
 */

import type { FallbackTier } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";

export interface KhaoYaiStatus {
  id: string;
  name: string;
  nameEn: string;
  unesco: boolean;
  iucnCategory: string;
  areaKm2: number;
  annualVisitors: number;
  isOpenNow: boolean;
  peakSeason: boolean;
  distanceFromSikhioKm: number;
  alertLevel: "open" | "restricted" | "closed";
  note: string | null;
  source: string;
  observedAt: string;
}

interface Props {
  data: KhaoYaiStatus | null;
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
}

export function KhaoYaiPanel({ data, loading, ageMinutes, fallbackTier }: Props) {
  const status = data;

  if (loading && !status) {
    return (
      <div className="col">
        <div className="eyebrow">เขาใหญ่ / KHAO YAI</div>
        <div className="skeleton" style={{ height: 12, marginTop: 8 }} />
      </div>
    );
  }
  if (!status) return null;

  const statusColor = status.alertLevel === "open"
    ? "var(--good)"
    : status.alertLevel === "restricted" ? "var(--warn)" : "var(--bad)";
  const statusLabel = status.alertLevel === "open" ? "เปิด" : status.alertLevel === "restricted" ? "จำกัด" : "ปิด";

  return (
    <div className="col" style={{ gap: 4 }}>
      <PanelHeader
        title="เขาใหญ่ "
        fallbackTier={fallbackTier}
        ageMinutes={ageMinutes}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={{
          background: "var(--gold)",
          color: "var(--fixed-dark)",
          fontSize: "var(--size-eyebrow)",
          fontWeight: 700,
          padding: "2px 6px",
          letterSpacing: "0.05em",
        }}>
          UNESCO WHS
        </span>
        <span style={{
          background: "var(--bg-3)",
          color: "var(--text-2)",
          fontSize: "var(--size-eyebrow)",
          padding: "2px 4px",
        }}>
          IUCN {status.iucnCategory}
        </span>
        <span style={{ marginLeft: "auto", color: statusColor }} className="eyebrow">
          {statusLabel}
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px" }}>
        <span className="caption">นักท่องเที่ยว/ปี</span>
        <span className="mono caption" style={{ textAlign: "right" }}>
          {(status.annualVisitors / 1_000_000).toFixed(1)} ล้านคน
        </span>
        <span className="caption">พื้นที่</span>
        <span className="mono caption" style={{ textAlign: "right" }}>
          {status.areaKm2.toLocaleString()} ตร.กม.
        </span>
        <span className="caption">ระยะจากสีคิ้ว</span>
        <span className="mono caption" style={{ textAlign: "right" }}>
          ~{status.distanceFromSikhioKm} กม.
        </span>
        <span className="caption">ฤดูท่องเที่ยว</span>
        <span className="mono caption" style={{ textAlign: "right", color: status.peakSeason ? "var(--accent)" : "var(--text-3)" }}>
          {status.peakSeason ? "High Season" : "Low Season"}
        </span>
      </div>
      {status.note && (
        <div className="caption" style={{ marginTop: 4, color: "var(--text-2)", fontStyle: "italic" }}>
          {status.note}
        </div>
      )}
    </div>
  );
}
